import React, { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { MDText } from 'i18n-react';

import { Button, ChevronBoldRightIcon, CrossIcon } from '@motorway/mw-highway-code';
import { OptimizelyContext } from '@optimizely/react-sdk';

import { ConfigContext } from 'Context/config';
import { UserContext } from 'Context/user';

import { setCookie } from 'Utilities';
import { SP_HEADER_EVENTS } from 'Utilities/analytics/analyticsHeader';
import { EVENT, FLAG } from 'Utilities/analytics/optimizely/constants';
import { useOptimizelyTrack } from 'Utilities/analytics/optimizely/useOptimizelyTrack';
import { INCODE_POST_SALE_SLUG } from 'Utilities/routing';

import { useBreakpoint } from 'Components/misc/Breakpoints/Breakpoints';

import { getButtonLink } from './GetTheAppBanner.helpers';
import LocalTexts from './GetTheAppBanner.texts.json';

import styles from './GetTheAppBanner.scss';

const LocalT = new MDText(LocalTexts);

const TEXT = {
  cta: LocalT.translate('cta'),
  heading: LocalT.translate('heading'),
  subheading: LocalT.translate('subheading'),
};

export const GetTheAppBanner = () => {
  const { userState: user } = useContext(UserContext);
  const {
    configState: {
      affiliate,
      appClipUrl: sliplaneUrl,
      experimentDecisions,
      hideGetTheAppBanner,
    },
  } = useContext(ConfigContext);
  const [hideBanner, setHideBanner] = useState(hideGetTheAppBanner);

  const { OPTIMIZELY_TRACK } = useOptimizelyTrack();
  const { optimizely } = useContext(OptimizelyContext);
  const isGetTheAppBannerOn = experimentDecisions?.[FLAG.MW_0_2_GET_THE_APP_BANNER] === 'on';

  const { pathname } = useLocation();
  const isExcludedPath = affiliate?.showBranding || pathname.includes(INCODE_POST_SALE_SLUG);

  const breakpoints = useBreakpoint();
  const mobileBreakpoint = breakpoints.maxWidth.breakpointTabletMobile;

  const shouldHideBanner = isExcludedPath || hideBanner || !isGetTheAppBannerOn;

  useEffect(() => {
    optimizely?.onReady().then(() => {
      if (!shouldHideBanner && mobileBreakpoint) {
        optimizely.decide(FLAG.MW_0_2_GET_THE_APP_BANNER);
        SP_HEADER_EVENTS.GET_THE_APP_LOADED();
      }
    });
  }, [shouldHideBanner, optimizely, mobileBreakpoint]);

  if (shouldHideBanner) {
    return null;
  }

  const buttonLink = getButtonLink({ loginToken: user?.login_token, pathname, sliplaneUrl });

  const handleOnClick = () => {
    SP_HEADER_EVENTS.GET_THE_APP_BANNER_CLICKED(buttonLink);
    OPTIMIZELY_TRACK(EVENT.GET_THE_APP_BANNER_CLICKED);
  };

  const handleOnClose = () => {
    SP_HEADER_EVENTS.GET_THE_APP_BANNER_CLOSED();
    OPTIMIZELY_TRACK(EVENT.GET_THE_APP_BANNER_HIDDEN);
    setCookie('hideGetTheAppBanner', 'true', 365);
    setHideBanner(true);
  };

  return (
    <section className={styles.banner}>
      <div className={styles.content}>
        <Button as='button' icon={CrossIcon} label="Close" onClick={handleOnClose} showLabel={false} size='small' variant='tertiary'/>
        <div className={styles.chevron}>
          <ChevronBoldRightIcon size='medium'/>
        </div>
        <div className={styles.text}>
          <span className={styles.heading}>{TEXT.heading}</span>
          <span className={styles.subheading}>{TEXT.subheading}</span>
        </div>
      </div>
      <Button as='a' href={buttonLink} label={TEXT.cta} onClick={handleOnClick} rel='noopener noreferrer' size='small' target='_blank'/>
    </section>
  );
};
